import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { getRoot } from 'mobx-easy';
import moment from 'moment';
import { DataQuerySorting, GetAllRoutesMonitoring, RoutesMonitoringResponse, Run, Task } from '@bringg/types';

import RootStore from 'bringg-web/stores/root-store';
import { getActionableWaypoint } from 'bringg-web/stores/tasks/task-utils';
import { FilterQueryObject } from '../top-section/types';
import { routesMonitoringLoaderProvider } from '../utils/routes-monitoring-loader';
import { routesMonitoringRootStore } from './routes-monitoring-root-store';
import RoutesMonitoringViewStore from './routes-monitoring-view-store';
import { detailedRoutesReducer } from '../utils/mapper';

export interface DetailedRun extends Run {
	vehicle_name?: string;
	fulfillment: {
		canceled: number;
		completed: number;
		partial_completed: number;
	};
	total_cost: number;
	eta_to_home: string;
}

class RoutesMonitoringStore {
	private disposeSignal: AbortSignal;

	routesMonitoringViewStore: RoutesMonitoringViewStore;

	runs: RoutesMonitoringResponse[] = [];
	tasksByRun: Map<number, Map<number, Task>> = new Map();
	detailedRuns: DetailedRun[] = [];
	filterQuery: FilterQueryObject = {};

	// This range should only be for traversing the data, and it's not supposed to change when pagination is used
	range: { from: string; to: string };

	// This range for the pagination to be used
	currentPageRange: { from: string; to: string };
	loading: boolean;
	refreshing: boolean;
	refreshAC: AbortController;

	prevId?: number;
	nextId?: number;
	refreshOptions?: {
		from?: number;
		to?: number;
		orderByDirection?: DataQuerySorting['direction'];
	} = {};
	fromTotalIndex = 0;

	hasNextPage = false;
	hoveredRoute: number = null;

	constructor(disposeSignal: AbortSignal) {
		makeObservable(this, {
			runs: observable,
			tasksByRun: observable.shallow,
			loading: observable,
			refreshing: observable,
			filterQuery: observable,
			detailedRuns: observable,
			range: observable,
			currentPageRange: observable,
			hoveredRoute: observable,
			setLoading: action,
			setTasksByRun: action,
			setHoveredRoute: action,
			tasks: computed,
			fetchNewPage: action,
			setFilterQuery: action,
			reset: action,
			refreshData: action,
			setDetailedRuns: action,
			fetchRestOfData: action,
			resetOnFilter: action,
			resetRangeOnDateFilterChanged: action,

			fromTotalIndex: observable,
			hasNextPage: observable,
			hasPrevPage: computed,
			hasPagination: computed,
			fetchNextPage: action,
			fetchPrevPage: action
		});

		this.routesMonitoringViewStore = routesMonitoringRootStore.getStore().routesMonitoringViewStore;

		this.disposeSignal = disposeSignal;
		this.refreshAC = new AbortController();

		reaction(
			() => this.filterQuery?.date,
			() => this.resetRangeOnDateFilterChanged(),
			{ fireImmediately: true, signal: disposeSignal }
		);

		this.disposeSignal.addEventListener(
			'abort',
			() => {
				this.refreshAC.abort();
			},
			{ once: true }
		);
	}

	private fetchAllTasksData = async () => {
		if (!this.runs.length) {
			this.setTasksByRun(new Map());
			return;
		}

		const tasksByRun = await routesMonitoringLoaderProvider.tasks(this.runs, {
			signal: this.disposeSignal
		});

		this.setTasksByRun(tasksByRun);
	};

	reset = () => {
		this.runs = [];
		this.setTasksByRun(new Map());
	};

	setTasksByRun = (tasksByRun: Map<number, Map<number, Task>>) => {
		this.tasksByRun = tasksByRun;
	};

	setLoading = (loading: boolean) => {
		this.loading = loading;
	};

	setFilterQuery = (query: FilterQueryObject) => {
		this.filterQuery = query;
	};

	refreshData = async (force = false) => {
		if (force && this.refreshing) {
			this.refreshAC.abort();
			this.refreshAC = new AbortController();

			this.refreshing = false;
		}

		if (this.loading || this.refreshing) {
			return;
		}

		this.refreshing = true;
		const { hasNextPage, routes } = await routesMonitoringLoaderProvider.routes(
			{
				...this.filterQuery,
				...this.currentPageRange
			},
			{
				...this.refreshOptions,
				signal: this.refreshAC.signal
			}
		);

		this.runs = routes;

		try {
			await Promise.all([this.fetchAllTasksData(), this.fetchDetailedRuns()]);
			this.setDetailedRuns();
		} catch (error) {
			console.error('failed to fetch refreshed data', error);
		}

		runInAction(() => {
			this.hasNextPage = hasNextPage;

			this.refreshing = false;
		});
	};

	init = async () => {
		return this.fetchNewPage({
			range: this.range,
			type: 'next',
			fromStart: true
		});
	};

	fetchAfterFilterUpdated = async () => {
		this.resetOnFilter();
		await this.fetchNewPage({
			range: this.range,
			type: 'next',
			fromStart: true
		});
		this.routesMonitoringViewStore.resetScroll();
	};

	async fetchNewPage({
		type,
		range,
		orderByDirection,
		fromId,
		toId,
		fromStart
	}: {
		type: 'prev' | 'next';
		range: { from: string; to: string };
		orderByDirection?: 'asc' | 'desc';
		fromId?: number;
		toId?: number;
		fromStart: boolean;
	}) {
		if (this.loading) {
			return;
		}

		if (this.refreshing) {
			this.refreshAC.abort();
			this.refreshAC = new AbortController();
		}

		this.setLoading(true);

		let hasNextPage: boolean;
		let routes: GetAllRoutesMonitoring;
		let prevId: number;
		let nextId: number;

		const prevRoutesSize = fromStart ? 0 : this.runs.length;

		try {
			({ hasNextPage, routes, prevId, nextId } = await routesMonitoringLoaderProvider.routes(
				{
					...this.filterQuery,
					...range
				},
				{
					orderByDirection,
					from: fromId,
					to: toId,
					signal: this.disposeSignal
				}
			));
		} catch (e) {
			this.setLoading(false);
			throw e;
		}

		runInAction(() => {
			if (fromStart) {
				this.fromTotalIndex = 0;
			}

			this.refreshOptions = {
				orderByDirection,
				from: fromId,
				to: toId
			};
			this.prevId = prevId;
			this.nextId = nextId;
			this.currentPageRange = range;
			this.runs = routes;

			if (type === 'next') {
				this.hasNextPage = hasNextPage;
				this.fromTotalIndex = Math.max(this.fromTotalIndex + prevRoutesSize, 1);
			} else {
				// If we did a prev page it means we have a next page
				this.hasNextPage = true;

				if (hasNextPage) {
					this.fromTotalIndex = this.fromTotalIndex - this.runs.length;
				} else {
					this.fromTotalIndex = 1;

					// Reached the first page so we need to reset the refresh options so we won't be in prev mode
					this.refreshOptions = {};
				}
			}
		});

		this.fetchRestOfData().catch(e => {
			console.error('failed to fetch routes monitoring data', e);
			this.setLoading(false);
		});
	}

	async fetchNextPage() {
		try {
			const lastLoadedRun = this.runs[this.runs.length - 1];
			const lastRun = getRoot<RootStore>().data.runStore.get(lastLoadedRun.id);

			await this.fetchNewPage({
				type: 'next',
				range: {
					from: lastRun.scheduled_start_time || lastRun.started_at,
					to: this.range.to
				},
				orderByDirection: 'asc',
				fromId: this.nextId,
				fromStart: false
			});
			this.routesMonitoringViewStore.resetScroll();
		} catch (e) {
			console.error('failed to fetch next page', e);
		}
	}

	async fetchPrevPage() {
		try {
			const firstLoadedRun = this.runs[0];
			const firstRun = getRoot<RootStore>().data.runStore.get(firstLoadedRun.id);
			await this.fetchNewPage({
				type: 'prev',
				range: {
					from: this.range.from,
					to: firstRun.scheduled_start_time || firstRun.started_at
				},
				orderByDirection: 'desc',
				toId: this.prevId,
				fromStart: false
			});

			this.routesMonitoringViewStore.resetScroll();
		} catch (e) {
			console.error('failed to fetch prev page', e);
		}
	}

	get hasPrevPage() {
		return this.fromTotalIndex > 1;
	}

	get toTotalIndex() {
		return this.fromTotalIndex + this.runs.length - 1;
	}

	get hasPagination() {
		return this.hasNextPage || this.hasPrevPage;
	}

	resetOnFilter() {
		this.refreshOptions = undefined;
		this.prevId = undefined;
		this.nextId = undefined;
		this.currentPageRange = { ...this.range };
	}

	resetRangeOnDateFilterChanged() {
		const date = this.filterQuery.date;
		this.range = {
			from: moment(date).startOf('day').toISOString(),
			to: moment(date).endOf('day').toISOString()
		};

		// Reset the pagination
		this.currentPageRange = { ...this.range };
	}

	fetchRestOfData = async () => {
		await Promise.all([
			getRoot<RootStore>().data.vehiclesStore.loadAll(),
			routesMonitoringLoaderProvider.plannedRoutes(this.runs, {
				signal: this.disposeSignal
			}),
			this.fetchAllTasksData(),
			this.fetchDetailedRuns()
		]);

		routesMonitoringRootStore.getStore().routeMonitoringTimelineViewStore.shouldZoom = true;
		this.setDetailedRuns();
		this.setLoading(false);
	};

	fetchDetailedRuns = async () => {
		if (this.runs.length === 0) {
			return;
		}

		await getRoot<RootStore>()
			.data.runStore.loadMany(
				{
					ids: this.runs.map(run => run.id)
				},
				{ useCache: true },
				{ signal: this.disposeSignal }
			)
			.catch(e => {
				console.error('failed to fetch detailed runs ', e);
			});
	};

	setDetailedRuns = () => {
		const runs = getRoot<RootStore>()
			.data.runStore.getByIds(this.runs.map(run => run.id))
			.filter(Boolean);

		this.detailedRuns = detailedRoutesReducer(runs, this.tasksByRun);
	};

	get tasks(): Task[] {
		return [...this.tasksByRun.values()].reduce((acc, t) => {
			acc.push(...t.values());
			return acc;
		}, []);
	}

	setHoveredRoute = (routeId: number) => {
		this.hoveredRoute = routeId;
	};

	isRouteInvalid = (routeId: number): boolean =>
		Array.from(this.tasksByRun.get(routeId)?.values() || []).some(task => {
			const waypoint = getActionableWaypoint(task);
			return !waypoint.eta;
		});

	getFirstTask = (routeId: number): Task => {
		return this.tasksByRun.get(routeId)?.values().next().value;
	};
}

export default RoutesMonitoringStore;
